<script setup lang="ts">
    import { computed } from 'vue';
    import { Bar } from 'vue-chartjs';
    import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from 'chart.js';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import { useRouter, useRoute } from 'vue-router';
    import type { SmsSent } from '../../interfaces';
    import { SMS_STATISTICS_EVENT_TYPES, getSmsStatisticsLabelMap } from '../sms-statistics-types';
    import { chartColor } from '@shared/modules/chart-colors';

    type NumericSmsSentKey = { [K in keyof SmsSent]: SmsSent[K] extends number ? K : never }[keyof SmsSent];
    type CountKey = NumericSmsSentKey;
    const countKeys: Record<string, CountKey> = {
        sent: 'SentCount',
        pending: 'PendingCount',
        delivered: 'ReceivedCount',
        clicked: 'ClickedCount',
        bounced: 'BouncedCount',
    };
    const pctKeys: Record<string, CountKey> = {
        sent: 'SentPercentage',
        pending: 'PendingPercentage',
        delivered: 'ReceivedPercentage',
        clicked: 'ClickedPercentage',
        bounced: 'BouncedPercentage',
    };

    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);
    applyChartDefaults();

    const props = defineProps<{
        overview: SmsSent | null;
    }>();

    const router = useRouter();
    const route = useRoute();

    const contactTypes = SMS_STATISTICS_EVENT_TYPES.map((t) => t.contactType);
    const barColors = SMS_STATISTICS_EVENT_TYPES.map((t) => chartColor(t.color));

    function fmt(val: number): string {
        return val > 0 && val < 1 ? val.toFixed(1) : Math.round(val).toString();
    }

    function navigateToContacts(index: number) {
        if (index < 0 || index >= contactTypes.length) return;
        router.push({
            name: 'TextMessageStatisticsContacts',
            params: { smsMessageId: route.params.smsMessageId, contactType: contactTypes[index] },
            query: { ...route.query },
        });
    }

    function getLabelIndexAtEvent(chart: any, event: any): number {
        const evt = event.native as MouseEvent;
        if (!evt || !chart) return -1;
        const xScale = chart.scales.x;
        const rect = chart.canvas.getBoundingClientRect();
        const x = evt.clientX - rect.left;
        const y = evt.clientY - rect.top;
        if (y >= xScale.top && y <= xScale.bottom) {
            return xScale.getValueForPixel(x) as number;
        }
        return -1;
    }

    const labelPointerPlugin = {
        id: 'labelPointer',
        afterEvent(chart: any, args: any) {
            const { type } = args.event;
            const index = getLabelIndexAtEvent(chart, args.event);
            if (type === 'mousemove' && index >= 0) {
                chart.canvas.style.cursor = 'pointer';
            } else if (type === 'click' && index >= 0) {
                navigateToContacts(index);
            }
        },
    };

    const chartData = computed(() => {
        if (!props.overview) return { labels: [], datasets: [] };
        const o = props.overview;

        const labelMap = getSmsStatisticsLabelMap();
        const rows = SMS_STATISTICS_EVENT_TYPES.map((t) => ({
            label: labelMap[t.key],
            count: o[countKeys[t.key]],
            pct: o[pctKeys[t.key]],
        }));

        return {
            labels: rows.map((r) => [r.label, `${r.count} (${fmt(r.pct)}%)`]),
            datasets: [
                {
                    data: rows.map((r) => parseFloat(fmt(r.pct))),
                    backgroundColor: barColors,
                },
            ],
        };
    });

    const chartOptions = computed(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: () => '' } },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { weight: 'bold' as const } },
            },
            y: {
                min: 0,
                max: 100,
                grid: { color: chartColor('grid'), lineWidth: (ctx: any) => (ctx.tick.value === 0 ? 2 : 1) },
                ticks: { callback: (value: number | string) => value + '%' },
            },
        },
        onClick: (event: unknown, elements: { index: number }[], chart: any) => {
            if (elements.length) {
                navigateToContacts(elements[0].index);
                return;
            }
            navigateToContacts(getLabelIndexAtEvent(chart, event));
        },
    }));
</script>

<template>
    <div class="print-block">
        <h2 class="h3">{{ $Resources.IssueStatisticsSummaryTab }}</h2>
        <div class="chart-container chart-container-panel h-400">
            <Bar :data="chartData" :options="chartOptions" :plugins="[labelPointerPlugin]" />
        </div>

        <div class="table-responsive m-lg-t">
            <table class="table table-hover">
                <thead>
                    <tr>
                        <th>{{ $Resources.SmsCountryLabel }}</th>
                        <th class="text-right">{{ $Resources.SmsRecipinetsLabel }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="cr in overview?.CountryRecipients ?? []" :key="cr.CountryCode">
                        <td>{{ cr.Country }} ({{ cr.CountryCode }})</td>
                        <td class="text-right">{{ cr.RecipientCount }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
